import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  type NotificationsResponseDto,
  type NotificationResponseDto,
} from "@tabipla/shared";
import { notificationKeys } from "./query-keys";
import type { UseNotificationsOptions, UseNotificationMutationOptions } from "./types";

/**
 * 通知一覧を取得するフック
 *
 * @example
 * // 全通知を取得
 * const { data } = useNotifications({ apiClient });
 *
 * // 特定のタイプのみ取得
 * const { data } = useNotifications({
 *   apiClient,
 *   filterTypes: ["new_follower"],
 * });
 */
export function useNotifications(options: UseNotificationsOptions) {
  const { apiClient, filterTypes, refetchInterval = 30000, enabled = true } = options;

  return useQuery({
    queryKey: notificationKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<NotificationsResponseDto>("/api/notifications");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch notifications");
      }

      const data = response.data!;

      // フィルタが指定されている場合はフィルタリング
      if (filterTypes && filterTypes.length > 0) {
        const filtered = data.notifications.filter((n) => filterTypes.includes(n.type));
        return {
          notifications: filtered,
          unreadCount: filtered.filter((n) => !n.read).length,
        };
      }

      return data;
    },
    refetchInterval,
    enabled,
  });
}

/**
 * 通知を既読にするフック
 */
export function useMarkNotificationRead(options: UseNotificationMutationOptions) {
  const { apiClient } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await apiClient.patch<NotificationResponseDto>(
        `/api/notifications/${notificationId}/read`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to mark notification as read");
      }
      return response.data!;
    },
    onSuccess: (updatedNotification) => {
      queryClient.setQueryData<NotificationsResponseDto>(notificationKeys.list(), (old) => {
        if (!old) return old;
        return {
          notifications: old.notifications.map((n) =>
            n.id === updatedNotification.id ? updatedNotification : n
          ),
          unreadCount: Math.max(0, old.unreadCount - 1),
        };
      });
    },
  });
}

/**
 * 全通知を既読にするフック
 */
export function useMarkAllNotificationsRead(options: UseNotificationMutationOptions) {
  const { apiClient } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<null>("/api/notifications/read-all");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to mark all as read");
      }
    },
    onSuccess: () => {
      queryClient.setQueryData<NotificationsResponseDto>(notificationKeys.list(), (old) => {
        if (!old) return old;
        return {
          notifications: old.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        };
      });
    },
  });
}

/**
 * 通知を削除するフック
 */
export function useDeleteNotification(options: UseNotificationMutationOptions) {
  const { apiClient } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await apiClient.delete<null>(`/api/notifications/${notificationId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete notification");
      }
    },
    onSuccess: (_, notificationId) => {
      queryClient.setQueryData<NotificationsResponseDto>(notificationKeys.list(), (old) => {
        if (!old) return old;
        const deletedNotification = old.notifications.find((n) => n.id === notificationId);
        return {
          notifications: old.notifications.filter((n) => n.id !== notificationId),
          unreadCount: deletedNotification?.read === false ? old.unreadCount - 1 : old.unreadCount,
        };
      });
    },
  });
}
